import React, { useState, useEffect, useRef } from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  VStack,
  Text,
  HStack,
  Progress,
  Box,
} from "@chakra-ui/react";
import { FiClock, FiLogOut, FiRefreshCw } from "react-icons/fi";
import { refreshToken, logOut } from "../services/authService";
import { showErrorAlert } from "../utils/alert";

// Add CSS for animations
const animationStyles = `
  @keyframes pulse {
    0%, 100% {
      opacity: 1;
      transform: scale(1);
    }
    50% {
      opacity: 0.7;
      transform: scale(1.1);
    }
  }

  @keyframes slideInScale {
    from {
      opacity: 0;
      transform: scale(0.95);
    }
    to {
      opacity: 1;
      transform: scale(1);
    }
  }

  .pulse-icon {
    animation: pulse 1s ease-in-out infinite;
  }

  .modal-enter {
    animation: slideInScale 0.3s ease-out;
  }
`;

// Inject styles
if (typeof document !== 'undefined') {
  const styleSheet = document.createElement('style');
  styleSheet.textContent = animationStyles;
  document.head.appendChild(styleSheet);
}

const SessionWarningModal = ({ isOpen, onClose, onRefreshSuccess, onLogout }) => {
  const [countdown, setCountdown] = useState(60); // 60 seconds = 1 minute
  const [isRefreshing, setIsRefreshing] = useState(false);
  const countdownIntervalRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      setCountdown(60); // Reset countdown when modal opens
      
      // Start countdown timer
      countdownIntervalRef.current = setInterval(() => {
        setCountdown((prev) => {
          if (prev <= 1) {
            // Time's up - logout
            clearInterval(countdownIntervalRef.current);
            handleLogout();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      return () => {
        if (countdownIntervalRef.current) {
          clearInterval(countdownIntervalRef.current);
        }
      };
    } else {
      // Clear interval when modal closes
      if (countdownIntervalRef.current) {
        clearInterval(countdownIntervalRef.current);
      }
    }
  }, [isOpen]);

  const handleContinueSession = async () => {
    setIsRefreshing(true);
    try {
      const result = await refreshToken();
      // Clear countdown interval
      if (countdownIntervalRef.current) {
        clearInterval(countdownIntervalRef.current);
      }
      onRefreshSuccess?.();
      onClose();
    } catch (error) {
      if (error?.code === "REFRESH_RATE_LIMIT" || error?.code === "REFRESH_BACKOFF") {
        showErrorAlert(
          error.message || "Please wait before refreshing your session again.",
        );
        return;
      }
      handleLogout();
    } finally {
      setIsRefreshing(false);
    }
  };

  const handleLogout = () => {
    if (countdownIntervalRef.current) {
      clearInterval(countdownIntervalRef.current);
    }
    onLogout?.();
    logOut();
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  const progressValue = (countdown / 60) * 100;

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => {}} // Prevent closing by clicking outside
      closeOnOverlayClick={false}
      closeOnEsc={false}
      isCentered
      size="md"
      motionPreset="slideInCenter"
    >
      <ModalOverlay bg="blackAlpha.700" backdropFilter="blur(4px)" />
      <ModalContent
        boxShadow="0 20px 60px rgba(0, 0, 0, 0.3)"
        borderRadius="lg"
        className="modal-enter"
      >
        <ModalHeader borderBottomWidth="1px" borderColor="gray.200">
          <HStack spacing={3}>
            <Box className={countdown <= 10 ? "pulse-icon" : ""}>
              <FiClock size={24} color={countdown <= 10 ? "red" : "orange"} />
            </Box>
            <Text fontSize="xl" fontWeight="700" color="gray.800">Session Expiring</Text>
          </HStack>
        </ModalHeader>

        <ModalBody py={6}>
          <VStack spacing={6} align="stretch">
            <Text fontSize="sm" color="gray.600" textAlign="center" fontWeight="500">
              Your session expires in:
            </Text>

            <Box textAlign="center" py={6} bg="gray.50" borderRadius="md">
              <Text
                fontSize="5xl"
                fontWeight="900"
                color={countdown <= 10 ? "red.600" : "orange.600"}
                fontFamily="mono"
                letterSpacing="wide"
              >
                {formatTime(countdown)}
              </Text>
              <Text fontSize="xs" color="gray.500" mt={2}>Click "Continue" to stay logged in</Text>
            </Box>

            <Progress
              value={progressValue}
              colorScheme={countdown <= 10 ? "red" : "orange"}
              size="md"
              borderRadius="4px"
            />
          </VStack>
        </ModalBody>

        <ModalFooter borderTopWidth="1px" borderColor="gray.200" pt={6}>
          <HStack spacing={3} w="100%">
            <Button
              flex={1}
              variant="outline"
              onClick={handleLogout}
              isDisabled={isRefreshing}
              leftIcon={<FiLogOut />}
              colorScheme="red"
              size="md"
              fontWeight="600"
            >
              Logout
            </Button>
            <Button
              flex={1}
              colorScheme="blue"
              onClick={handleContinueSession}
              isLoading={isRefreshing}
              loadingText="Refreshing..."
              leftIcon={<FiRefreshCw />}
              size="md"
              fontWeight="600"
              _hover={{
                transform: "translateY(-2px)",
                boxShadow: "0 4px 12px rgba(66, 153, 225, 0.4)",
              }}
              transition="all 0.2s"
            >
              Continue
            </Button>
          </HStack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default SessionWarningModal;

